import { useTenancy } from '@/contexts/TenancyContext';
import { useState, useEffect } from 'react';
import { getCompanySettings, getCompanyInfo } from '@/services/companyService';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

// DED-605: Helper to fetch business units from customerOrg.teams.businessUnitNames
const getSharedBusinessUnits = async (companyId: string): Promise<string[]> => {
  try {
    const companyInfo = await getCompanyInfo(companyId);
    if (companyInfo?.customerOrg?.teams?.businessUnitNames) {
      const businessUnitNames = companyInfo.customerOrg.teams.businessUnitNames as Record<string, { name: string }>;
      return Object.values(businessUnitNames)
        .filter(bu => bu && bu.name)
        .map(bu => bu.name);
    }
    return [];
  } catch (error) {
    console.error('Error fetching shared business units:', error);
    return [];
  }
};

const defaultMenus: Record<string, any> = {
  "documentLibrary": {
    "documentTypes": [
      "Policy", 
      "Procedure", 
      "Form", 
      "Evidence", 
      "Matrix", 
      "Checklist", 
      "Standard", 
      "Confluence Page"
    ],
    "classifications": ["Customer Confidential", "Business Confidential", "Public"],
    "businessUnits": ["Management", "Operations", "Development", "Finance"]
  },
  "updatedAt": {
    "__time__": "2025-06-12T03:36:01.984Z"
  }
}

/**
 * Fetches menu settings via Thalassa API
 * @param companyId - The company ID
 * @param menuType - The type of menu to fetch (e.g., 'documentLibrary', 'risks')
 * @returns Promise that resolves to the requested menu settings
 */
export const getMenuSettings = async (companyId: string, menuType: string) => {
  try {
    // Use company settings API to get menus
    const menusData = await getCompanySettings(companyId, 'menus');
    
    let menuData: Record<string, any> = {};
    
    if (menusData && menusData[menuType]) {
      menuData = menusData[menuType];
    } else if (defaultMenus[menuType]) {
      menuData = defaultMenus[menuType];
    }
    
    // DED-605: For documentLibrary, override businessUnits with shared business units from customerOrg
    if (menuType === 'documentLibrary') {
      const sharedBusinessUnits = await getSharedBusinessUnits(companyId);
      if (sharedBusinessUnits.length > 0) {
        menuData = {
          ...menuData,
          businessUnits: sharedBusinessUnits
        };
      }
    }
    
    if (Object.keys(menuData).length === 0) {
      console.warn(`No menu settings found for ${menuType}, returning empty object`);
    }
    
    return menuData;
  } catch (error) {
    console.error(`Error fetching ${menuType} menu settings:`, error);
    throw error;
  }
};

/**
 * React hook to get menu settings
 * @param menuType - The type of menu to fetch (e.g., 'documentLibrary', 'risks')
 * @returns Object containing menu settings, loading state, and error
 */
export const useMenuSettings = (menuType: string) => {
  const { activeTenancy } = useTenancy();
  const [settings, setSettings] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchMenuSettings = async () => {
      if (!activeTenancy?.id) return;
      
      try {
        setIsLoading(true);
        const data = await getMenuSettings(activeTenancy.id, menuType);
        setSettings(data);
      } catch (err) {
        console.error(`Failed to load ${menuType} menu settings:`, err);
        setError(err instanceof Error ? err : new Error(`Failed to load ${menuType} menu settings`));
      } finally {
        setIsLoading(false);
      }
    };

    fetchMenuSettings();
  }, [activeTenancy?.id, menuType]);

  return { settings, isLoading, error };
};

/**
 * Gets a specific menu option by key
 * @param menuType - The type of menu (e.g., 'documentLibrary')
 * @param optionKey - The key of the option to get (e.g., 'documentTypes', 'statuses')
 * @param companyId - Optional company ID, will use active tenancy if not provided
 * @returns Promise that resolves to the requested menu options array
 */
export const getMenuOptions = async (
  menuType: string,
  optionKey: string,
  companyId?: string
): Promise<Array<{ key: string; value: string }>> => {
  try {
    const effectiveCompanyId = companyId || window.companyID;
    if (!effectiveCompanyId) {
      throw new Error('No company ID provided and none found in window.companyID');
    }

    const menuSettings = await getMenuSettings(effectiveCompanyId, menuType);
    const options = menuSettings[optionKey] || [];
    
    // Convert string array to {key, value} objects if needed
    return Array.isArray(options) 
      ? options.map(opt => typeof opt === 'string' ? { key: opt, value: opt } : opt)
      : [];
  } catch (error) {
    console.error(`Error getting ${optionKey} options:`, error);
    return [];
  }
};

/**
 * Saves menu settings via Thalassa API
 * @param companyId - The company ID
 * @param menuType - The type of menu to save (e.g., 'documentLibrary', 'audit')
 * @param menuData - The menu data to save
 * @returns Promise that resolves when the menu is saved
 */
export const saveMenuSettings = async (companyId: string, menuType: string, menuData: any) => {
  try {
    const response = await deiterateAPI.PUT('/v1/menus', {
      mode: 'cors',
      backend: 'thalassa',
      headers: {
        'x-tenancy-id': companyId
      },
      body: JSON.stringify({
        [menuType]: menuData
      })
    });

    if (response.statusCode !== 200) {
      throw new Error(response.error || 'Failed to save menu settings');
    }

    return response;
  } catch (error) {
    console.error(`Error saving ${menuType} menu settings:`, error);
    throw error;
  }
};

/**
 * Saves all menu settings at once
 * @param companyId - The company ID
 * @param allMenus - All menu data to save
 * @returns Promise that resolves when the menus are saved
 */
export const saveAllMenuSettings = async (companyId: string, allMenus: Record<string, any>) => {
  try {
    const response = await deiterateAPI.PUT('/v1/menus', {
      mode: 'cors',
      backend: 'thalassa',
      headers: {
        'x-tenancy-id': companyId
      },
      body: JSON.stringify(allMenus)
    });

    if (response.statusCode !== 200) {
      throw new Error(response.error || 'Failed to save menu settings');
    }

    return response;
  } catch (error) {
    console.error('Error saving menu settings:', error);
    throw error;
  }
};

export default {
  getMenuSettings,
  useMenuSettings,
  getMenuOptions,
  saveMenuSettings,
  saveAllMenuSettings
};
